import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router'
import { useUser } from '@clerk/clerk-react'
import {
  getAdminLead,
  getAdminLeads,
  updateLead,
  logInteraction,
  deleteInteraction,
  makeLeadPrimary,
  type Lead,
  type LeadDetail,
  type Interaction,
  type NewInteraction,
} from '../lib/api'

const ADMIN_EMAILS = ['matt@primateintelligence.ai', 'mehdi@primateintelligence.ai']

const CHANNEL_ICONS: Record<string, string> = {
  call: '📞',
  email: '✉',
  linkedin: '💼',
  meeting: '🤝',
  demo: '🖥',
  note: '📝',
  discovery_call: '🔍',
  other: '💬',
}

const CHANNELS = ['call', 'email', 'linkedin', 'meeting', 'demo', 'note', 'discovery_call', 'other'] as const
const OUTCOMES = [
  { value: 'interested', label: 'Interested' },
  { value: 'follow_up', label: 'Follow-up' },
  { value: 'not_interested', label: 'Not interested' },
  { value: 'no_response', label: 'No response' },
  { value: 'converted', label: 'Converted' },
  { value: 'unsubscribed', label: 'Unsubscribed' },
] as const

// ─── Helpers ──────────────────────────────────────────────────────────────────

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const days = Math.floor(diff / 86400000)
  if (days < 1) return 'today'
  if (days === 1) return '1d ago'
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function isPlaceholderEmail(email: string): boolean {
  return email?.includes('@placeholder.primateintelligence.ai') || false
}

function scoreBg(total: number | undefined): string {
  if (!total) return 'bg-carbon text-steel'
  if (total >= 22) return 'bg-green-500/20 text-green-400'
  if (total >= 18) return 'bg-yellow-500/20 text-yellow-400'
  return 'bg-carbon text-steel'
}

function outcomeBadge(outcome: string | null) {
  if (!outcome) return null
  const map: Record<string, string> = {
    interested: 'bg-green-500/20 text-green-400',
    converted: 'bg-green-500/20 text-green-400',
    follow_up: 'bg-signal/20 text-signal',
    no_response: 'bg-carbon text-steel',
    not_interested: 'bg-red-500/20 text-red-400',
    unsubscribed: 'bg-red-500/20 text-red-400',
  }
  return (
    <span className={`px-2 py-0.5 rounded text-footnote font-medium capitalize ${map[outcome] ?? 'bg-carbon text-steel'}`}>
      {outcome.replace(/_/g, ' ')}
    </span>
  )
}

// ─── Log Interaction Modal ────────────────────────────────────────────────────

interface LogInteractionModalProps {
  leadId: string
  onClose: () => void
  onSuccess: (interaction: Interaction) => void
}

function LogInteractionModal({ leadId, onClose, onSuccess }: LogInteractionModalProps) {
  const [channel, setChannel] = useState<NewInteraction['channel']>('call')
  const [direction, setDirection] = useState<'outbound' | 'inbound'>('outbound')
  const [notes, setNotes] = useState('')
  const [outcome, setOutcome] = useState<NewInteraction['outcome']>('no_response')
  const [occurredAt, setOccurredAt] = useState(() => new Date().toISOString().slice(0, 16))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Discovery call fields
  const [currentStack, setCurrentStack] = useState('')
  const [painPoints, setPainPoints] = useState('')
  const [desiredOutcome, setDesiredOutcome] = useState('')
  const [objections, setObjections] = useState('')
  const [questionsAsked, setQuestionsAsked] = useState('')
  const [budgetSignal, setBudgetSignal] = useState('')
  const [timeline, setTimeline] = useState('')
  const [demoRequested, setDemoRequested] = useState(false)
  const [designPartnerInterest, setDesignPartnerInterest] = useState('')
  const [winLoss, setWinLoss] = useState('')

  async function handleSave() {
    if (!notes.trim()) {
      setError('Notes are required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const metadata: Record<string, unknown> = {}
      if (channel === 'discovery_call') {
        if (currentStack) metadata.current_stack = currentStack
        if (painPoints) metadata.pain_points = painPoints
        if (desiredOutcome) metadata.desired_outcome = desiredOutcome
        if (objections) metadata.objections = objections
        if (questionsAsked) metadata.questions_asked = questionsAsked
        if (budgetSignal) metadata.budget_signal = budgetSignal
        if (timeline) metadata.timeline = timeline
        if (demoRequested) metadata.demo_requested = demoRequested
        if (designPartnerInterest) metadata.design_partner_interest = designPartnerInterest
        if (winLoss) metadata.win_loss = winLoss
      }
      const created = await logInteraction(leadId, {
        channel,
        direction,
        notes: notes.trim(),
        outcome,
        occurred_at: new Date(occurredAt).toISOString(),
        ...(Object.keys(metadata).length > 0 ? { metadata } : {}),
      })
      onSuccess(created)
      onClose()
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-void/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-obsidian border border-carbon rounded w-full max-w-lg max-h-[90vh] flex flex-col">
        <div className="p-5 border-b border-carbon flex items-center justify-between">
          <h2 className="text-h3 text-chalk">Log Interaction</h2>
          <button onClick={onClose} className="text-steel hover:text-chalk text-title-1 leading-none">&times;</button>
        </div>

        <div className="p-5 space-y-4 overflow-y-auto">
          {/* Channel */}
          <div>
            <label className="block text-footnote text-steel mb-2">Channel</label>
            <div className="flex flex-wrap gap-2">
              {CHANNELS.map(ch => (
                <button
                  key={ch}
                  onClick={() => setChannel(ch)}
                  className={`px-3 py-1.5 rounded text-footnote font-medium capitalize transition-colors ${
                    channel === ch
                      ? 'bg-signal text-white'
                      : 'bg-carbon/50 text-steel hover:text-chalk border border-carbon'
                  }`}
                >
                  {CHANNEL_ICONS[ch]} {ch}
                </button>
              ))}
            </div>
          </div>

          {/* Direction */}
          <div>
            <label className="block text-footnote text-steel mb-2">Direction</label>
            <div className="flex gap-2">
              {(['outbound', 'inbound'] as const).map(d => (
                <button
                  key={d}
                  onClick={() => setDirection(d)}
                  className={`px-3 py-1.5 rounded text-footnote font-medium capitalize transition-colors ${
                    direction === d
                      ? 'bg-signal text-white'
                      : 'bg-carbon/50 text-steel hover:text-chalk border border-carbon'
                  }`}
                >
                  {d === 'outbound' ? '↗ Outbound' : '↙ Inbound'}
                </button>
              ))}
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-footnote text-steel mb-1">Date & Time</label>
            <input
              type="datetime-local"
              value={occurredAt}
              onChange={e => setOccurredAt(e.target.value)}
              className="bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal"
            />
          </div>

          {/* Outcome */}
          <div>
            <label className="block text-footnote text-steel mb-1">Outcome</label>
            <select
              value={outcome}
              onChange={e => setOutcome(e.target.value as NewInteraction['outcome'])}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal"
            >
              {OUTCOMES.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-footnote text-steel mb-1">Notes <span className="text-red-400">*</span></label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="What happened? Key takeaways…"
              rows={4}
              className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal resize-none"
            />
          </div>

          {/* Discovery call structured fields */}
          {channel === 'discovery_call' && (
            <div className="border border-carbon/60 rounded p-4 space-y-4 bg-void/40">
              <div className="text-footnote text-signal font-medium uppercase tracking-wide">Discovery Call Details</div>

              <div>
                <label className="block text-footnote text-steel mb-1">Current stack</label>
                <input
                  type="text"
                  value={currentStack}
                  onChange={e => setCurrentStack(e.target.value)}
                  placeholder="What tools/systems are they using today?"
                  className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal"
                />
              </div>

              <div>
                <label className="block text-footnote text-steel mb-1">Pain points</label>
                <textarea
                  value={painPoints}
                  onChange={e => setPainPoints(e.target.value)}
                  placeholder="What's breaking or frustrating them?"
                  rows={2}
                  className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal resize-none"
                />
              </div>

              <div>
                <label className="block text-footnote text-steel mb-1">Desired outcome</label>
                <input
                  type="text"
                  value={desiredOutcome}
                  onChange={e => setDesiredOutcome(e.target.value)}
                  placeholder="What do they want to be able to do?"
                  className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal"
                />
              </div>

              <div>
                <label className="block text-footnote text-steel mb-1">Objections</label>
                <textarea
                  value={objections}
                  onChange={e => setObjections(e.target.value)}
                  placeholder="Why did they hesitate or say no?"
                  rows={2}
                  className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal resize-none"
                />
              </div>

              <div>
                <label className="block text-footnote text-steel mb-1">Questions asked</label>
                <textarea
                  value={questionsAsked}
                  onChange={e => setQuestionsAsked(e.target.value)}
                  placeholder="What did they ask us?"
                  rows={2}
                  className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk placeholder:text-steel/50 focus:outline-none focus:border-signal resize-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-footnote text-steel mb-1">Budget signal</label>
                  <select
                    value={budgetSignal}
                    onChange={e => setBudgetSignal(e.target.value)}
                    className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal"
                  >
                    <option value="">Unknown</option>
                    <option value="none">None</option>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div>
                  <label className="block text-footnote text-steel mb-1">Timeline</label>
                  <select
                    value={timeline}
                    onChange={e => setTimeline(e.target.value)}
                    className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal"
                  >
                    <option value="">Unknown</option>
                    <option value="immediate">Immediate</option>
                    <option value="3mo">3 months</option>
                    <option value="6mo">6 months</option>
                    <option value="12mo+">12 months+</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-footnote text-steel mb-1">Design partner interest</label>
                  <select
                    value={designPartnerInterest}
                    onChange={e => setDesignPartnerInterest(e.target.value)}
                    className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal"
                  >
                    <option value="">Unknown</option>
                    <option value="none">None</option>
                    <option value="low">Low</option>
                    <option value="high">High</option>
                  </select>
                </div>

                <div>
                  <label className="block text-footnote text-steel mb-1">Signal</label>
                  <select
                    value={winLoss}
                    onChange={e => setWinLoss(e.target.value)}
                    className="w-full bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal"
                  >
                    <option value="">— Not set —</option>
                    <option value="win">Win</option>
                    <option value="loss">Loss</option>
                    <option value="pending">Pending</option>
                    <option value="nurture">Nurture</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="demo_requested"
                  checked={demoRequested}
                  onChange={e => setDemoRequested(e.target.checked)}
                  className="w-4 h-4 rounded border-carbon bg-void text-signal focus:ring-signal"
                />
                <label htmlFor="demo_requested" className="text-footnote text-steel cursor-pointer">Demo requested?</label>
              </div>
            </div>
          )}

          {error && <div className="text-footnote text-red-400 bg-red-900/20 rounded px-3 py-2">{error}</div>}
        </div>

        <div className="p-5 border-t border-carbon flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-body-sm text-steel hover:text-chalk border border-carbon rounded transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-body-sm bg-signal text-white rounded hover:bg-signal/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving…' : 'Save Interaction'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Editable Field ───────────────────────────────────────────────────────────

interface EditableFieldProps {
  label: string
  value: string
  onSave: (val: string) => Promise<void>
  type?: 'text' | 'email' | 'url'
  isEmail?: boolean
}

function EditableField({ label, value, onSave, type = 'text', isEmail }: EditableFieldProps) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)

  async function handleSave() {
    setSaving(true)
    try {
      await onSave(draft)
      setEditing(false)
    } finally {
      setSaving(false)
    }
  }

  if (editing) {
    return (
      <div>
        <label className="block text-footnote text-steel mb-1">{label}</label>
        <div className="flex gap-2">
          <input
            type={type}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            className="flex-1 bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal"
            autoFocus
          />
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 text-footnote bg-signal text-white rounded disabled:opacity-50"
          >
            {saving ? '…' : '✓'}
          </button>
          <button
            onClick={() => { setEditing(false); setDraft(value) }}
            className="px-3 py-1.5 text-footnote text-steel hover:text-chalk border border-carbon rounded"
          >
            ✕
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      className="group cursor-pointer"
      onClick={() => { setDraft(value); setEditing(true) }}
    >
      <label className="block text-footnote text-steel mb-1 cursor-pointer">{label}</label>
      <div className={`text-body-sm flex items-center gap-1 ${
        isEmail && isPlaceholderEmail(value) ? 'text-red-400' : value ? 'text-chalk' : 'text-steel italic'
      }`}>
        {value || '—'}
        <span className="opacity-0 group-hover:opacity-50 text-footnote text-steel ml-1">✏</span>
      </div>
    </div>
  )
}

// ─── ICP Scorecard ────────────────────────────────────────────────────────────

interface IcpScorecardProps {
  scores: { pa?: number; tf?: number; ba?: number; tl?: number; bs?: number; total?: number }
}

const ICP_LABELS: Record<string, string> = {
  pa: 'Product Alignment',
  tf: 'Technical Fit',
  ba: 'Budget Authority',
  tl: 'Timeline',
  bs: 'Business Size',
}

function IcpScorecard({ scores }: IcpScorecardProps) {
  const dims = ['pa', 'tf', 'ba', 'tl', 'bs'] as const
  const total = scores.total ?? 0

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3 mb-4">
        <span className="text-body text-steel">ICP Total</span>
        <span className={`text-h2 font-bold ${
          total >= 22 ? 'text-green-400' : total >= 18 ? 'text-yellow-400' : 'text-steel'
        }`}>
          {total}/25
        </span>
      </div>

      {dims.map(dim => {
        const val = scores[dim] ?? 0
        const pct = (val / 5) * 100
        return (
          <div key={dim}>
            <div className="flex justify-between text-footnote mb-1">
              <span className="text-steel">{ICP_LABELS[dim]}</span>
              <span className={val >= 4 ? 'text-green-400' : val >= 3 ? 'text-yellow-400' : 'text-steel'}>
                {val}/5
              </span>
            </div>
            <div className="h-1.5 bg-carbon rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  val >= 4 ? 'bg-green-400' : val >= 3 ? 'bg-yellow-400' : 'bg-steel'
                }`}
                style={{ width: `${pct}%` }}
              />
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Interaction Timeline ─────────────────────────────────────────────────────

interface InteractionTimelineProps {
  interactions: Interaction[]
  onDelete: (id: string) => Promise<void>
}

function InteractionTimeline({ interactions, onDelete }: InteractionTimelineProps) {
  const [deleting, setDeleting] = useState<string | null>(null)

  async function handleDelete(id: string) {
    setDeleting(id)
    try {
      await onDelete(id)
    } finally {
      setDeleting(null)
    }
  }

  if (interactions.length === 0) {
    return <p className="text-steel text-body-sm">No interactions logged yet.</p>
  }

  return (
    <div className="space-y-3">
      {interactions.map(interaction => (
        <div key={interaction.id} className="border-l-2 border-carbon pl-4 pb-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-body">{CHANNEL_ICONS[interaction.channel] ?? '💬'}</span>
              <span className="text-body-sm font-medium text-chalk capitalize">{interaction.channel === 'discovery_call' ? 'Discovery Call' : interaction.channel}</span>
              <span className="text-footnote text-steel">
                {interaction.direction === 'inbound' ? '↙ inbound' : '↗ outbound'}
              </span>
              {outcomeBadge(interaction.outcome)}
              {interaction.channel === 'discovery_call' && interaction.metadata?.win_loss ? (
                <span className={`px-2 py-0.5 rounded text-footnote font-medium ${
                  (interaction.metadata.win_loss as string) === 'win' ? 'bg-green-500/20 text-green-400' :
                  (interaction.metadata.win_loss as string) === 'loss' ? 'bg-red-500/20 text-red-400' :
                  (interaction.metadata.win_loss as string) === 'nurture' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-steel/20 text-steel'
                }`}>
                  {String(interaction.metadata.win_loss as string).charAt(0).toUpperCase() + String(interaction.metadata.win_loss as string).slice(1)}
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-footnote text-steel">{timeAgo(interaction.occurred_at)}</span>
              <button
                onClick={() => void handleDelete(interaction.id)}
                disabled={deleting === interaction.id}
                className="text-footnote text-steel hover:text-red-400 transition-colors disabled:opacity-50"
              >
                {deleting === interaction.id ? '…' : '✕'}
              </button>
            </div>
          </div>
          {interaction.notes && (
            <p className="text-body-sm text-steel mt-1 leading-relaxed">{interaction.notes}</p>
          )}
          {interaction.channel === 'discovery_call' && (
            <div className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-footnote">
              {interaction.metadata?.current_stack ? <div><span className="text-steel/60">Stack:</span> <span className="text-steel">{String(interaction.metadata.current_stack as string)}</span></div> : null}
              {interaction.metadata?.budget_signal ? <div><span className="text-steel/60">Budget:</span> <span className="text-steel capitalize">{String(interaction.metadata.budget_signal as string)}</span></div> : null}
              {interaction.metadata?.timeline ? <div><span className="text-steel/60">Timeline:</span> <span className="text-steel">{String(interaction.metadata.timeline as string)}</span></div> : null}
              {interaction.metadata?.demo_requested ? <div><span className="text-steel/60">Demo:</span> <span className="text-signal">Requested</span></div> : null}
              {interaction.metadata?.design_partner_interest ? <div><span className="text-steel/60">Design partner:</span> <span className="text-steel capitalize">{String(interaction.metadata.design_partner_interest as string)}</span></div> : null}
            </div>
          )}
          <div className="text-footnote text-steel/50 mt-1">by {interaction.created_by}</div>
        </div>
      ))}
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function AdminLeadDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { user, isLoaded } = useUser()
  const [lead, setLead] = useState<LeadDetail | null>(null)
  const [interactions, setInteractions] = useState<Interaction[]>([])
  const [companyContacts, setCompanyContacts] = useState<Lead[]>([])
  const [loading, setLoading] = useState(true)
  const [showLogModal, setShowLogModal] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [makingPrimary, setMakingPrimary] = useState<string | null>(null)

  const email = user?.primaryEmailAddress?.emailAddress ?? user?.emailAddresses?.[0]?.emailAddress ?? ''
  const isAdmin = isLoaded && !!user && ADMIN_EMAILS.includes(email)

  useEffect(() => {
    if (!isAdmin || !id) return
    setLoading(true)
    getAdminLead(id)
      .then(async data => {
        setLead(data)
        setInteractions(data.interactions)
        // Fetch sibling contacts for same company
        if (data.company) {
          try {
            const all = await getAdminLeads({ status: 'all' })
            const siblings = all.filter(l => l.company === data.company)
            setCompanyContacts(siblings)
          } catch { /* non-fatal */ }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [isAdmin, id])

  async function handleMakePrimary(contactId: string) {
    setMakingPrimary(contactId)
    try {
      await makeLeadPrimary(contactId)
      setCompanyContacts(prev => prev.map(c => ({ ...c, is_primary: c.id === contactId })))
    } catch { /* silent */ } finally { setMakingPrimary(null) }
  }

  async function handleUpdate(fields: Record<string, unknown>) {
    if (!id || !lead) return
    setSaveError(null)
    try {
      const updated = await updateLead(id, fields)
      setLead(updated)
    } catch (err) {
      setSaveError((err as Error).message)
    }
  }

  async function handleDeleteInteraction(interactionId: string) {
    await deleteInteraction(interactionId)
    setInteractions(prev => prev.filter(i => i.id !== interactionId))
  }

  if (!isLoaded) return <div className="min-h-screen bg-void" />

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <p className="text-body text-steel">Access denied</p>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-signal" />
      </div>
    )
  }

  if (!lead) {
    return (
      <div className="p-8">
        <button onClick={() => navigate('/dashboard/leads')} className="text-signal hover:underline text-body-sm">
          ← Back to Leads
        </button>
        <p className="text-steel text-body mt-4">Lead not found.</p>
      </div>
    )
  }

  const ce = lead.clay_enrichment ?? {}

  return (
    <>
      {showLogModal && (
        <LogInteractionModal
          leadId={lead.id}
          onClose={() => setShowLogModal(false)}
          onSuccess={interaction => setInteractions(prev => [interaction, ...prev])}
        />
      )}

      <div className="p-8 space-y-8 max-w-4xl">

        {/* Breadcrumb */}
        <button
          onClick={() => navigate('/dashboard/leads')}
          className="text-signal hover:underline text-body-sm"
        >
          ← Back to Leads
        </button>

        {/* Page header */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-h1 text-chalk">{lead.company ?? '—'}</h1>
            <div className="flex items-center gap-3 mt-1 flex-wrap">
              {lead.status && (
                <span className="px-2 py-0.5 rounded text-footnote bg-carbon text-steel capitalize">{lead.status}</span>
              )}
              {ce.vertical && (
                <span className="px-2 py-0.5 rounded text-footnote bg-signal/10 text-signal font-medium">{ce.vertical}</span>
              )}
              {ce.icp_score?.total !== undefined && (
                <span className={`px-2 py-0.5 rounded text-footnote font-semibold ${scoreBg(ce.icp_score?.total)}`}>
                  ICP {ce.icp_score.total}
                </span>
              )}
              {/* Primary badge for this specific contact */}
              {companyContacts.find(c => c.id === lead.id)?.is_primary && (
                <span className="px-2 py-0.5 rounded text-footnote bg-signal/15 text-signal font-semibold">★ Primary</span>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2 shrink-0">
            <button
              onClick={() => navigate(`/dashboard/admin/emails?lead_id=${lead.id}`)}
              className="px-4 py-2 text-body-sm text-signal hover:text-signal/80 border border-signal/30 rounded transition-colors"
            >
              Draft Email →
            </button>
            <button
              onClick={() => setShowLogModal(true)}
              className="px-4 py-2 text-body-sm bg-signal text-white rounded hover:bg-signal/90 transition-colors"
            >
              Log Interaction
            </button>
          </div>
        </div>

        {saveError && (
          <div className="text-body-sm text-red-400 bg-red-900/20 rounded px-4 py-3">{saveError}</div>
        )}

        <div className="grid grid-cols-1 tablet:grid-cols-3 gap-6">

          {/* Left column: Contact info + About */}
          <div className="tablet:col-span-2 space-y-6">

            {/* Section 1: Contact Info */}
            <div className="bg-obsidian border border-carbon rounded p-5">
              <h2 className="text-body font-semibold text-chalk mb-4">Contact Info</h2>
              <div className="grid grid-cols-1 gap-4 tablet:grid-cols-2">
                <EditableField
                  label="Full Name"
                  value={lead.full_name ?? ''}
                  onSave={v => handleUpdate({ full_name: v })}
                />
                <EditableField
                  label="Email"
                  value={lead.email ?? ''}
                  onSave={v => handleUpdate({ email: v })}
                  type="email"
                  isEmail
                />
                <EditableField
                  label="Company"
                  value={lead.company ?? ''}
                  onSave={v => handleUpdate({ company: v })}
                />
                <EditableField
                  label="Title"
                  value={ce.contact_title ?? ''}
                  onSave={v => handleUpdate({ contact_title: v })}
                />
                <EditableField
                  label="Website"
                  value={ce.website ?? ''}
                  onSave={v => handleUpdate({ website: v })}
                  type="url"
                />
                <EditableField
                  label="LinkedIn URL"
                  value={ce.linkedin_url ?? ''}
                  onSave={v => handleUpdate({ linkedin_url: v })}
                  type="url"
                />
              </div>
            </div>

            {/* Section 2: Company Contacts (shown when there are multiple contacts for this company) */}
            {companyContacts.length > 0 && (
              <div className="bg-obsidian border border-carbon rounded p-5">
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-body font-semibold text-chalk">Company Contacts</h2>
                  <span className="text-footnote text-steel">{companyContacts.length} contact{companyContacts.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="space-y-2">
                  {companyContacts.map(contact => (
                    <div key={contact.id} className="flex items-center gap-3 py-1.5 border-b border-carbon/40 last:border-0">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/dashboard/leads/${contact.id}`)}
                            className={`text-body-sm font-medium hover:underline ${
                              contact.id === lead.id ? 'text-signal' : 'text-chalk'
                            }`}
                          >
                            {contact.full_name ?? '—'}
                          </button>
                          {contact.is_primary && (
                            <span className="px-1.5 py-0.5 text-footnote bg-signal/15 text-signal rounded font-semibold leading-none">★ Primary</span>
                          )}
                          {contact.id === lead.id && !contact.is_primary && (
                            <span className="px-1.5 py-0.5 text-footnote bg-carbon text-steel rounded leading-none">Viewing</span>
                          )}
                        </div>
                        {contact.contact_title && (
                          <div className="text-footnote text-steel mt-0.5">{contact.contact_title}</div>
                        )}
                        {contact.email && !contact.email.includes('@placeholder') && (
                          <div className="text-footnote text-steel/70">{contact.email}</div>
                        )}
                      </div>
                      {/* Make Primary button — only for non-primary contacts when viewing a different contact OR always accessible */}
                      {!contact.is_primary && (
                        <button
                          onClick={() => void handleMakePrimary(contact.id)}
                          disabled={makingPrimary === contact.id}
                          className="px-2 py-1 text-footnote text-steel hover:text-chalk border border-carbon rounded transition-colors disabled:opacity-40 shrink-0"
                        >
                          {makingPrimary === contact.id ? '…' : 'Make Primary'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section 3: About */}
            <div className="bg-obsidian border border-carbon rounded p-5">
              <h2 className="text-body font-semibold text-chalk mb-4">About</h2>
              <div className="space-y-4">
                <EditableField
                  label="What they do"
                  value={ce.what_they_do ?? ''}
                  onSave={v => handleUpdate({ what_they_do: v })}
                />
                <EditableField
                  label="Vertical"
                  value={ce.vertical ?? ''}
                  onSave={v => handleUpdate({ vertical: v })}
                />
                <EditableField
                  label="Next Action"
                  value={ce.next_action ?? ''}
                  onSave={v => handleUpdate({ next_action: v })}
                />
                <EditableField
                  label="Introduced By"
                  value={ce.introduced_by ?? ''}
                  onSave={v => handleUpdate({ introduced_by: v })}
                />
                <div className="cursor-pointer group">
                  <label className="block text-footnote text-steel mb-1">Status</label>
                  <select
                    value={lead.status ?? 'prospect'}
                    onChange={e => void handleUpdate({ status: e.target.value })}
                    className="bg-void border border-carbon rounded px-3 py-2 text-body-sm text-chalk focus:outline-none focus:border-signal"
                  >
                    <option value="prospect">Prospect</option>
                    <option value="engaged">Engaged</option>
                    <option value="disqualified">Disqualified</option>
                    <option value="converted">Converted</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Section 5: Interaction Timeline */}
            <div className="bg-obsidian border border-carbon rounded p-5">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-body font-semibold text-chalk">Interactions</h2>
                <span className="text-footnote text-steel">{interactions.length} total</span>
              </div>
              <InteractionTimeline
                interactions={interactions}
                onDelete={handleDeleteInteraction}
              />
            </div>
          </div>

          {/* Right column: ICP Scorecard */}
          <div className="space-y-6">
            <div className="bg-obsidian border border-carbon rounded p-5">
              <h2 className="text-body font-semibold text-chalk mb-4">ICP Scorecard</h2>
              {ce.icp_score ? (
                <IcpScorecard scores={ce.icp_score} />
              ) : (
                <p className="text-steel text-body-sm">No ICP score available.</p>
              )}
            </div>

            {/* Quick meta */}
            <div className="bg-obsidian border border-carbon rounded p-5 space-y-3">
              <h2 className="text-body font-semibold text-chalk mb-2">Details</h2>
              <div>
                <div className="text-footnote text-steel">Source</div>
                <div className="text-body-sm text-chalk capitalize">{lead.source ?? '—'}</div>
              </div>
              <div>
                <div className="text-footnote text-steel">Added</div>
                <div className="text-body-sm text-chalk">
                  {new Date(lead.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </div>
              </div>
              {lead.score && (
                <div>
                  <div className="text-footnote text-steel">Score</div>
                  <div className="text-body-sm text-chalk">{lead.score}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
